import { ReportStatus } from "../../../api/types";
import * as client from "../../../client";
import StatusNames from "../../../mappings_dir/statuses.json";
import LuminorAppetiteInfo from "../../../mappings_dir/luminor_appetite.json";
import IcmConstructionTypeMapping from "../../../mappings_dir/icm_construction_types.json";
import { mmddyyyyToDate } from "../../../date-utils";
import { SafeReportMetadataResponse } from "../Conversion";

export const TEMP_PIPELINE_EXAMPLES = new Set([
  "BentallGreenOak (U.S.) Limited Partnership",
  "Marathon Patent Group Inc",
]);

export const readableStatus = (status: ReportStatus): string => {
  return StatusNames.statuses[status];
};

export const statusIsClosedOrDeclined = (status: client.status): boolean => {
  return [
    "declined",
    "closed",
    "closed_bor",
    "not_reviewed",
    "quoted_not_taken_up",
  ].includes(status);
};

export type OpenStage = "open_quote" | "open_indication" | "in_progress";

export const OPEN_STAGE_GROUP_LABEL: { [stage in OpenStage]: string } = {
  open_quote: "Open Quotes",
  open_indication: "Open Indications",
  in_progress: "In Progress Accounts",
};

export const OTHER_SUBMISSIONS_GROUP_LABEL = "Other Submissions";

// A submission is sectioned by its workflow stage only while its effective date
// is still in the future: "pending", "modeling_complete", "referral_sent" and
// "flagged_for_review" accounts are in progress, "indicated" and "quoted" accounts are open
// indications/quotes. A submission whose effective date has passed is no longer
// live and falls out of these sections.
// `today` should be midnight so an effective date of today counts as not-live.
export const openStageForReport = (
  report: SafeReportMetadataResponse,
  today: Date
): OpenStage | null => {
  if (
    report.status !== "quoted" &&
    report.status !== "indicated" &&
    report.status !== "pending" &&
    report.status !== "modeling_complete" &&
    report.status !== "referral_sent" &&
    report.status !== "flagged_for_review"
  ) {
    return null;
  }
  const effectiveDate = mmddyyyyToDate(
    report.report_metadata.company_info.effective_date
  );
  if (!effectiveDate || effectiveDate <= today) {
    return null;
  }
  switch (report.status) {
    case "quoted":
      return "open_quote";
    case "indicated":
      return "open_indication";
    default:
      return "in_progress";
  }
};

export const readableAppetiteFromScore = (score: number): string => {
  switch (score) {
    case 1:
      return "Out of Appetite";
    case 2:
      return "Missing Required Fields";
    case 3:
      return "Uncertain";
    case 4:
      return "Build";
    case 5:
      return "In Appetite";
  }
  return "Unknown Appetite";
};

export const numericAppetiteScore = (appetite: client.appetite_result) => {
  return LuminorAppetiteInfo.appetite_score[appetite];
};

export const readableAppetiteShort = (appetite: client.appetite_result) => {
  return LuminorAppetiteInfo.appetite_string_short[appetite];
};

export const readableAppetiteLong = (appetite: client.appetite_result) => {
  return LuminorAppetiteInfo.appetite_string_long[appetite];
};

export const getAllStatuses = (): client.status[] => {
  return Object.values(client.status);
};

// Whether creating a referral draft should offer to move the report to
// "referral_sent". Only the working statuses that precede a referral qualify;
// a report already referred, indicated, quoted or closed out is left alone.
export const offersReferralSentStatus = (status: client.status): boolean => {
  const referralPrecursors: client.status[] = [
    "cleared",
    "pending",
    "modeling_complete",
  ];
  return referralPrecursors.includes(status);
};

export const isClearanceStatus = (status: client.status): boolean => {
  const clearanceStatuses: client.status[] = [
    "cleared",
    "not_cleared_effective_date_too_far",
    "not_cleared_incomplete_submission",
    "not_cleared_out_of_appetite",
    "not_cleared_unapproved_broker",
    "not_cleared_prior_submission",
    "not_cleared_low_tiv",
    "not_cleared_foreign_exposure",
  ];
  return clearanceStatuses.includes(status);
};

// Shared cell padding for DataTable columns across dashboards.
export const columnPtOptions = {
  bodyCell: { style: { padding: "6px 8px" } },
};

export type Severity = "good" | "bad" | "medium" | "other" | "neutral";

export const premiumSeverity = (premium: number): Severity => {
  if (premium >= 10_000_000) return "bad";
  if (premium >= 1_000_000) return "medium";
  if (premium >= 100_000) return "other";
  return "good";
};

export const colorForAttributes = (
  severity: Severity,
  isLightMode: boolean
) => {
  switch (severity) {
    case "good":
      return isLightMode ? "var(--green-100)" : "#0e4f2644";
    case "bad":
      return isLightMode ? "var(--red-100)" : "#66181444";
    case "medium":
      return isLightMode ? "var(--yellow-100)" : "#5e480344";
    case "other":
      return isLightMode ? "var(--cyan-100)" : "#18346244";
    default:
      return "var(--surface-100)";
  }
};

export const borderColorForAttributes = (
  severity: Severity,
  isLightMode: boolean
) => {
  switch (severity) {
    case "good":
      return isLightMode ? "var(--green-400)" : "var(--green-700)";
    case "bad":
      return isLightMode ? "var(--red-400)" : "var(--red-700)";
    case "medium":
      return isLightMode ? "var(--yellow-400)" : "var(--yellow-700)";
    case "other":
      return isLightMode ? "var(--cyan-400)" : "var(--cyan-700)";
    default:
      return "var(--surface-300)";
  }
};

export const colorForConfidence = (
  confidence: string,
  isLightMode: boolean
) => {
  switch (confidence) {
    case "low":
      return isLightMode
        ? HIGHLIGHT_ERROR_LIGHT_MODE
        : HIGHLIGHT_ERROR_DARK_MODE;
    case "medium":
      return isLightMode
        ? HIGHLIGHT_WARNING_LIGHT_MODE
        : HIGHLIGHT_WARNING_DARK_MODE;
    case "user edited":
      return isLightMode
        ? HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE
        : HIGHLIGHT_USER_OVERRIDE_DARK_MODE;
    default:
      return undefined;
  }
};

export const HIGHLIGHT_ERROR_DARK_MODE = "var(--red-900)";
export const HIGHLIGHT_WARNING_DARK_MODE = "var(--yellow-900)";
export const HIGHLIGHT_USER_OVERRIDE_DARK_MODE = "var(--blue-800)";
export const HIGHLIGHT_ERROR_LIGHT_MODE = "var(--red-100)";
export const HIGHLIGHT_WARNING_LIGHT_MODE = "var(--yellow-200)";
export const HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE = "var(--blue-100)";

export const LOW_CONFIDENCE_MINIMUM_SEVERITY = 80;

export const flaggedHighlightColor = (
  maxSeverity: number,
  isLightMode: boolean,
  hasOverride: boolean,
  isRequiredAndIsBlank?: boolean
) => {
  // Required and blank takes precedence over edits
  if (isRequiredAndIsBlank) {
    if (isLightMode) {
      return HIGHLIGHT_ERROR_LIGHT_MODE;
    }
    return HIGHLIGHT_ERROR_DARK_MODE;
  }

  // Edits take precedence over flags
  if (hasOverride) {
    if (isLightMode) {
      return HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE;
    }
    return HIGHLIGHT_USER_OVERRIDE_DARK_MODE;
  }

  // Now show flags
  if (maxSeverity > LOW_CONFIDENCE_MINIMUM_SEVERITY) {
    if (isLightMode) {
      return HIGHLIGHT_ERROR_LIGHT_MODE;
    }
    return HIGHLIGHT_ERROR_DARK_MODE;
  }
  if (isLightMode) {
    return HIGHLIGHT_WARNING_LIGHT_MODE;
  }
  return HIGHLIGHT_WARNING_DARK_MODE;
};

export const icmConstructionTypeMapping = () => {
  return IcmConstructionTypeMapping.construction_types;
};
